import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

type MentionListProps = {
  items: string[];
  command: (props: { id: string }) => void;
};

export type MentionListRef = {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
  getSelectedIndex: () => number;
  selectItem: (index: number) => void;
};

// MentionList component for rendering mention suggestions
export const MentionList = forwardRef<MentionListRef, MentionListProps>(
  (props, ref) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const [selectedIndex, setSelectedIndex] = useState(0);
    // Use a ref to track the actual current index to avoid stale closures
    const currentIndexRef = useRef(0);

    // Keep the ref updated with state
    useEffect(() => {
      currentIndexRef.current = selectedIndex;
    }, [selectedIndex]);

    const selectItem = (index: number) => {
      if (index < 0 || index >= props.items.length) {
        return;
      }
      const item = props.items[index];
      if (item) {
        props.command({ id: item });
      }
    };

    // Reset selection when items change
    useEffect(() => {
      setSelectedIndex(0);
      currentIndexRef.current = 0;
    }, [props.items]);

    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }) => {
        if (event.key === 'ArrowUp') {
          // Calculate new index and update both state and ref
          const newIndex =
            (currentIndexRef.current + props.items.length - 1) %
            props.items.length;
          currentIndexRef.current = newIndex;
          setSelectedIndex(newIndex);
          return true;
        }

        if (event.key === 'ArrowDown') {
          // Calculate new index and update both state and ref
          const newIndex = (currentIndexRef.current + 1) % props.items.length;
          currentIndexRef.current = newIndex;
          setSelectedIndex(newIndex);
          return true;
        }

        if (event.key === 'Enter') {
          // Always use the ref value for selection
          selectItem(currentIndexRef.current);
          return true;
        }

        return false;
      },
      getSelectedIndex: () => currentIndexRef.current,
      selectItem,
    }));

    return (
      <div
        className="mention-list"
        style={{
          background: 'white',
          border: '1px solid #ddd',
          borderRadius: '4px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          padding: '6px',
          maxHeight: '200px',
          overflow: 'auto',
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            selectItem(currentIndexRef.current);
          }
        }}
      >
        {props.items.length ? (
          props.items.map((item, index) => (
            <button
              style={{
                padding: '4px 8px',
                cursor: 'pointer',
                display: 'block',
                width: '100%',
                textAlign: 'left',
                border: 'none',
                background: index === selectedIndex ? '#e6f7ff' : 'transparent',
                borderRadius: '2px',
              }}
              key={index}
              onClick={(e) => {
                e.preventDefault();
                // Update both the ref and state
                currentIndexRef.current = index;
                setSelectedIndex(index);
                selectItem(index);
              }}
            >
              {item}
            </button>
          ))
        ) : (
          <div style={{ padding: '4px 8px' }}>{t('noResults')}</div>
        )}
      </div>
    );
  }
);

MentionList.displayName = 'MentionList';
